<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { apiFetch } from '../utils/apiFetch'
import { useSettingsStore } from '../stores/settingsStore'

const settingsStore = useSettingsStore()
const loading = ref(true)
const visibleSections = ref({
  has: () => true,
  add: () => {}
})

const serviceImages = [
  '/images/services/inspection.png',
  '/images/services/3d-design.png',
  '/images/services/prefab.png',
  '/images/services/installation.png',
  '/images/services/warranty.png',
  '/images/services/support.png'
]

const settings = ref({
   services_hero_title: 'บริการออกแบบและติดตั้ง <br/> แบบครบวงจร',
   services_hero_subtitle: 'บริการระดับมาตรฐานสากลจาก CR Distribution',
   services_hero_desc: 'เราพร้อมส่งมอบประสบการณ์ที่ดีเยี่ยม ตั้งแต่การให้คำปรึกษา การสั่งซื้อ การจัดส่ง จนถึงการติดตั้งด้วยทีมงานมืออาชีพ',
   services_items: [],
   services_cta_title: 'เริ่มต้นเนรมิตพื้นที่ของคุณวันนี้',
   services_cta_desc: 'ติดต่อทีมงานเพื่อรับคำปรึกษาฟรี เรายินดีให้บริการและมอบสิ่งที่ดีที่สุดให้แก่คุณเสมอ',
   services_content_rich: '',
   services_hero_bg: '',
   services_comparison_title: 'ทำไมต้องเลือกบริการออกแบบติดตั้งของเรา?',
   services_comparison_subtitle: 'เปรียบเทียบมาตรฐาน',
   services_comparison_desc: 'ยกระดับงานบริการติดตั้งโรงเรือนสู่มาตรฐานระดับสากล ปราณีตและแตกต่างอย่างเห็นได้ชัด',
   services_cad_filename: 'cr_model_active.dwg',
   services_cad_snap_status: '3D SNAP ENABLED',
   services_cad_steel_thickness: '2.3mm',
   services_cad_wind_load: '120 KM/H OK',
   services_cad_spec_struct: 'PREFAB MOD',
   services_cad_roof_cover: 'POLY 150MIC',
   services_cad_qc_status: 'PASSED ISO'
})

const stats = ref([
  { number: '2,500+', label: 'โปรเจคที่ติดตั้งสำเร็จ' },
  { number: '10 ปี', label: 'รับประกันโครงสร้าง' },
  { number: '48 ชม.', label: 'ติดตั้งเสร็จสิ้น' },
  { number: '98%', label: 'ลูกค้าพึงพอใจ' }
])

const comparisonItems = ref([
  { feature: '1. ระยะเวลาดำเนินงานจริงหน้างาน', us: 'ประกอบเสร็จสมบูรณ์ภายใน 48 ชม. (ระบบ Prefab)', them: 'ใช้เวลา 2-3 สัปดาห์ (ตัดแต่งเชื่อมหน้างานทีละชิ้น)' },
  { feature: '2. มลภาวะและสภาพแวดล้อมหน้างาน', us: 'ระบบ Modular Joint ไร้เสียงเชื่อม ไร้สะเก็ดไฟและคราบเขม่า', them: 'เสียงดังรบกวน มีควันเชื่อม และกองเศษเหล็กขยะทิ้งไว้' },
  { feature: '3. คุณภาพวัสดุและกรรมวิธีเคลือบกันสนิม', us: 'เหล็กกัลวาไนซ์หนาพิเศษ อบสีฝุ่นด้วยความร้อน Powder Coat', them: 'ทาสีกันสนิมเกรดทั่วไป ลอกร่อนง่าย เกิดสนิมใน 1-2 ปี' },
  { feature: '4. ความปลอดภัยและการคำนวณโครงสร้าง', us: 'คำนวณผ่าน CAD โดยวิศวกรโครงสร้าง รองรับลมพายุรุนแรง', them: 'สร้างตามความคุ้นชินหน้างาน มีความเสี่ยงหากพายุแรง' },
  { feature: '5. การรับประกันและเอกสารสิทธิ์สัญญา', us: 'สัญญารับประกันโครงสร้างสูงสุด 10 ปี เอกสารชัดเจน', them: 'ไม่มีสัญญา หรือรับประกันปากเปล่าสั้นๆ ยุ่งยากเมื่อเกิดชำรุด' }
])

let observer = null

const setupObserver = () => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        visibleSections.value.add(entry.target.dataset.section)
      }
    })
  }, { threshold: 0.15 })
  document.querySelectorAll('[data-section]').forEach(el => observer.observe(el))
}

const loadSettings = async () => {
  try {
    const res = await apiFetch('/api/settings')
    const data = await res.json()
    if (data.success && data.data) {
      if (data.data.services_hero_title) settings.value.services_hero_title = data.data.services_hero_title
      if (data.data.services_hero_subtitle) settings.value.services_hero_subtitle = data.data.services_hero_subtitle
      if (data.data.services_hero_desc) settings.value.services_hero_desc = data.data.services_hero_desc
      if (data.data.services_items) {
         try { settings.value.services_items = JSON.parse(data.data.services_items) } catch(e) {}
      }
      if (data.data.services_cta_title) settings.value.services_cta_title = data.data.services_cta_title
      if (data.data.services_cta_desc) settings.value.services_cta_desc = data.data.services_cta_desc
      if (data.data.services_content_rich) settings.value.services_content_rich = data.data.services_content_rich
      if (data.data.services_hero_bg) settings.value.services_hero_bg = data.data.services_hero_bg

      // Load new stats from DB
      if (data.data.services_stats) {
         try { stats.value = JSON.parse(data.data.services_stats) } catch(e) {}
      }

      // Load new comparisons from DB
      if (data.data.services_comparison_title) settings.value.services_comparison_title = data.data.services_comparison_title
      if (data.data.services_comparison_subtitle) settings.value.services_comparison_subtitle = data.data.services_comparison_subtitle
      if (data.data.services_comparison_desc) settings.value.services_comparison_desc = data.data.services_comparison_desc
      if (data.data.services_comparison_items) {
         try { comparisonItems.value = JSON.parse(data.data.services_comparison_items) } catch(e) {}
      }

      // Load CAD Settings
      if (data.data.services_cad_filename) settings.value.services_cad_filename = data.data.services_cad_filename
      if (data.data.services_cad_snap_status) settings.value.services_cad_snap_status = data.data.services_cad_snap_status
      if (data.data.services_cad_steel_thickness) settings.value.services_cad_steel_thickness = data.data.services_cad_steel_thickness
      if (data.data.services_cad_wind_load) settings.value.services_cad_wind_load = data.data.services_cad_wind_load
      if (data.data.services_cad_spec_struct) settings.value.services_cad_spec_struct = data.data.services_cad_spec_struct
      if (data.data.services_cad_roof_cover) settings.value.services_cad_roof_cover = data.data.services_cad_roof_cover
      if (data.data.services_cad_qc_status) settings.value.services_cad_qc_status = data.data.services_cad_qc_status
    }
  } catch (error) {
    console.error('Failed to load settings:', error)
  } finally {
    loading.value = false
    setTimeout(setupObserver, 100)
  }
}

const getStepTechLabel = (index) => {
  const labels = [
    'PHASE 01 / FIELD SURVEY & ANALYSIS',
    'PHASE 02 / 3D CAD ARCHITECTURE',
    'PHASE 03 / CNC PREFAB PRODUCTION',
    'PHASE 04 / RAPID ASSEMBLY SYSTEM',
    'PHASE 05 / 10-YEAR SEAL OF QUALITY',
    'PHASE 06 / CONTINUOUS CARE HUB'
  ]
  return labels[index] || `PHASE 0${index + 1} / SERVICE STEP`
}

const getStepChecklist = (index) => {
  const checklists = [
    [
      'สำรวจหน้างานโดยทีมวิศวกรวิเคราะห์ทิศทางลม ฐานราก และหน้าดิน',
      'ออกแบบตำแหน่งติดตั้งตามทิศทางการขึ้นลงของดวงอาทิตย์',
      'ให้คำปรึกษาการปรับสภาพดินและระบบสาธารณูปโภคเบื้องต้น'
    ],
    [
      'สร้างโมเดล 3 มิติเสมือนจริงให้เห็นภาพโครงสร้างกับบ้านของท่าน',
      'ปรับปรุงแบบตามต้องการก่อนดำเนินการผลิตจริง ปรับสีได้ตามต้องการ',
      'ทีมวิศวกรโครงสร้างลงนามรับรองแบบตามมาตรฐาน'
    ],
    [
      'ผลิตชิ้นส่วนสำเร็จรูป Prefab จากโรงงานควบคุมคุณภาพไร้ฝุ่น',
      'เคลือบสีฝุ่นระบบ Powder Coat ทนทานความชื้นภายนอก',
      'จัดส่งรวดเร็วด้วยกล่องแพ็คเกจป้องกันการขีดข่วนชิ้นส่วน 100%'
    ],
    [
      'ประกอบหน้างานด้วยระบบ Modular Joint แข็งแรงและเร็ว',
      'การันตีการประกอบแล้วเสร็จภายใน 48 ชั่วโมง ไร้งานเชื่อมเหล็กเลอะเทอะ',
      'เก็บกวาดความสะอาด คืนพื้นที่หลังการทำงานทันที'
    ],
    [
      'ออกเอกสารการรับประกันโครงสร้างหลักยาวนานสูงสุด 10 ปี',
      'รับประกันวัสดุแผ่นมุงหลังคาและการรั่วซึมนาน 1 ปีเต็ม',
      'ตรวจสอบโครงสร้างซ้ำโดยวิศวกรก่อนส่งมอบงานอย่างเป็นทางการ'
    ],
    [
      'บริการ Support แชท/โทร สอบถามความต้องการ 24 ชั่วโมง',
      'พร้อมบริการขยับขยาย ต่อเติม หรือย้ายพื้นที่ติดตั้งในอนาคต',
      'ทีมบำรุงรักษาและให้คำแนะนำเรื่องสภาพพืชพรรณภายในโรงเรือน'
    ]
  ]
  return checklists[index] || []
}

onMounted(() => loadSettings())
onUnmounted(() => { if (observer) observer.disconnect() })
</script>

<template>
  <div class="bg-slate-50 dark:bg-[#060a0f] min-h-screen transition-colors duration-500 overflow-x-hidden relative">
    
    <!-- Engineering Grid Pattern Background -->
    <div class="absolute inset-0 bg-[linear-gradient(to_right,#00000004_1px,transparent_1px),linear-gradient(to_bottom,#00000004_1px,transparent_1px)] bg-[size:3rem_3rem] dark:bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] pointer-events-none"></div>

    <div v-if="loading" class="flex justify-center items-center min-h-screen">
       <div class="relative flex items-center justify-center">
         <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
         <div class="absolute w-6 h-6 rounded-full bg-emerald-500/20 animate-pulse"></div>
       </div>
    </div>
    
    <div v-else>

      <!-- ═══ HERO SECTION ═══ -->
      <section class="relative min-h-[90vh] flex items-center justify-center pt-24 pb-28 lg:py-0 overflow-hidden">
        <!-- Background gradients & textures -->
        <div class="absolute inset-0 bg-gradient-to-br from-emerald-50/50 via-slate-50 to-teal-50/30 dark:from-[#08131a] dark:via-[#060a0f] dark:to-[#050f14] transition-colors duration-500"></div>
        <div class="absolute inset-0 opacity-5 dark:opacity-10 pointer-events-none" :style="{ backgroundImage: 'url(' + (settings.services_hero_bg || '/images/services/warranty.png') + ')', backgroundSize: 'cover', backgroundPosition: 'center' }"></div>
        
        <!-- Glowing dynamic orbs -->
        <div class="absolute top-1/4 left-1/10 w-80 h-80 bg-emerald-500/10 dark:bg-emerald-500/5 rounded-full blur-[100px] animate-float-slow"></div>
        <div class="absolute bottom-1/4 right-1/10 w-[450px] h-[450px] bg-teal-500/10 dark:bg-teal-500/5 rounded-full blur-[130px] animate-float-medium" style="animation-delay: 1.5s"></div>
        
        <div class="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          <!-- Hero Left Column (Content) -->
          <div class="lg:col-span-7 text-left flex flex-col items-start">
            <div class="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 dark:border-emerald-400/20 backdrop-blur-md mb-6 animate-pulse">
              <span class="w-2.5 h-2.5 rounded-full bg-emerald-500 dark:bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></span>
              <span class="text-emerald-700 dark:text-emerald-300 text-xs font-bold tracking-wider uppercase" v-html="settings.services_hero_subtitle"></span>
            </div>
            
            <h1 class="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight leading-none text-slate-900 dark:text-white mb-6 font-heading" v-html="settings.services_hero_title"></h1>
            
            <p class="text-base sm:text-lg md:text-xl text-slate-600 dark:text-slate-300 font-light leading-relaxed max-w-xl mb-8">
              {{ settings.services_hero_desc }}
            </p>
            
            <div class="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <router-link to="/quotation" class="group inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-500 hover:to-teal-600 text-white font-bold py-4 px-8 rounded-2xl shadow-lg shadow-emerald-600/15 hover:shadow-emerald-500/25 transition-all duration-300 hover:-translate-y-0.5">
                ขอใบเสนอราคาฟรี
                <svg class="w-5 h-5 group-hover:translate-x-1.5 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
              </router-link>
              <router-link to="/space-calculator" class="group inline-flex items-center justify-center gap-2.5 bg-white/80 dark:bg-slate-900/60 hover:bg-white dark:hover:bg-slate-900 backdrop-blur-md text-slate-700 dark:text-white font-bold py-4 px-8 rounded-2xl border border-slate-200/80 dark:border-slate-800 transition-all duration-300 hover:-translate-y-0.5 shadow-sm hover:shadow-md">
                <svg class="w-5 h-5 text-slate-400 group-hover:text-emerald-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>
                คำนวณพื้นที่ติดตั้ง
              </router-link>
            </div>
          </div>

          <!-- Hero Right Column (Interactive Blueprint CAD Preview) -->
          <div class="lg:col-span-5 relative w-full flex items-center justify-center lg:justify-end">
            <div class="relative w-full max-w-lg aspect-square flex items-center justify-center">
              <!-- Background Glow -->
              <div class="absolute inset-0 bg-gradient-to-tr from-emerald-500/20 to-teal-500/10 rounded-full blur-[60px] opacity-70"></div>
              
              <!-- Blueprint Box -->
              <div class="relative w-full h-[400px] rounded-3xl bg-slate-950/90 border border-slate-800 shadow-2xl p-6 overflow-hidden flex flex-col justify-between backdrop-blur-xl">
                <!-- Drafting Grid -->
                <div class="absolute inset-0 bg-[linear-gradient(to_right,#10b9810a_1px,transparent_1px),linear-gradient(to_bottom,#10b9810a_1px,transparent_1px)] bg-[size:1.25rem_1.25rem] pointer-events-none"></div>
                
                <!-- Terminal Header -->
                <div class="relative z-10 flex items-center justify-between border-b border-slate-900 pb-3">
                  <div class="flex items-center gap-1.5">
                    <span class="w-2.5 h-2.5 rounded-full bg-[#ff5f56]"></span>
                    <span class="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]"></span>
                    <span class="w-2.5 h-2.5 rounded-full bg-[#27c93f]"></span>
                    <span class="text-[10px] text-slate-500 font-mono ml-2">{{ settings.services_cad_filename || 'cr_model_active.dwg' }}</span>
                  </div>
                  <span class="text-[9px] text-emerald-400 font-mono bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">{{ settings.services_cad_snap_status || '3D SNAP ENABLED' }}</span>
                </div>

                <!-- CAD Graphic -->
                <div class="relative z-10 flex-1 flex items-center justify-center my-4">
                  <!-- Simulated Greenhouse drawing (SVG) -->
                  <svg class="w-56 h-40 text-emerald-400/80 drop-shadow-[0_0_8px_rgba(16,185,129,0.4)]" viewBox="0 0 200 150" fill="none" stroke="currentColor" stroke-width="1.25">
                    <!-- Base line -->
                    <line x1="20" y1="120" x2="180" y2="120" />
                    <!-- Columns -->
                    <line x1="30" y1="120" x2="30" y2="65" />
                    <line x1="60" y1="120" x2="60" y2="65" />
                    <line x1="100" y1="120" x2="100" y2="65" />
                    <line x1="140" y1="120" x2="140" y2="65" />
                    <line x1="170" y1="120" x2="170" y2="65" />
                    <!-- Arch roof trusses -->
                    <path d="M30 65 Q100 10 170 65" />
                    <!-- Inner arch profiles -->
                    <path d="M60 65 Q100 25 140 65" stroke-dasharray="1 1" opacity="0.6" />
                    <!-- Roof structural purlins -->
                    <line x1="30" y1="65" x2="170" y2="65" />
                    <line x1="45" y1="45" x2="155" y2="45" />
                    <line x1="75" y1="23" x2="125" y2="23" />
                    <!-- Cross bracing -->
                    <line x1="30" y1="92" x2="60" y2="65" stroke-width="0.75" opacity="0.8" />
                    <line x1="60" y1="120" x2="100" y2="92" stroke-width="0.75" opacity="0.8" />
                    <line x1="100" y1="92" x2="140" y2="65" stroke-width="0.75" opacity="0.8" />
                    <line x1="140" y1="120" x2="170" y2="92" stroke-width="0.75" opacity="0.8" />
                    <!-- Dimensions annotations -->
                    <line x1="20" y1="130" x2="180" y2="130" stroke-dasharray="2 2" stroke="#64748b" stroke-width="0.75" />
                    <path d="M20 127 L20 133 M180 127 L180 133" stroke="#64748b" stroke-width="0.75" />
                    <text x="100" y="138" fill="#94a3b8" font-size="7" font-family="monospace" text-anchor="middle">W: 6.00m (SCALE 1:50)</text>
                    
                    <line x1="12" y1="65" x2="12" y2="15" stroke-dasharray="2 2" stroke="#64748b" stroke-width="0.75" />
                    <path d="M9 65 L15 65 M9 15 L15 15" stroke="#64748b" stroke-width="0.75" />
                    <text x="6" y="40" fill="#94a3b8" font-size="7" font-family="monospace" text-anchor="middle" transform="rotate(-90 6 40)">H: 3.50m</text>
                  </svg>

                  <!-- UI Badge: Material Status -->
                  <div class="absolute top-2 right-2 bg-slate-900/95 border border-slate-800 p-2.5 rounded-xl flex flex-col gap-1 backdrop-blur shadow-xl transform translate-x-2 hover:translate-y-[-2px] transition-transform duration-300">
                    <span class="text-[8px] font-mono text-slate-400 uppercase">GALVANIZED STEEL</span>
                    <div class="flex items-center gap-1.5">
                      <div class="w-20 h-1 bg-slate-800 rounded-full overflow-hidden">
                        <div class="h-full bg-emerald-500 rounded-full w-[100%]"></div>
                      </div>
                      <span class="text-[9px] font-mono font-bold text-emerald-400">{{ settings.services_cad_steel_thickness || '2.3mm' }}</span>
                    </div>
                  </div>
                  
                  <!-- UI Badge: Compass -->
                  <div class="absolute bottom-2 left-2 flex items-center gap-2 bg-slate-900/95 border border-slate-800 p-2 rounded-xl backdrop-blur shadow-xl transform -translate-x-2 hover:translate-y-[2px] transition-transform duration-300">
                    <svg class="w-5 h-5 text-emerald-500 animate-spin" style="animation-duration: 25s" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <circle cx="12" cy="12" r="10" stroke-dasharray="2 2"/>
                      <line x1="12" y1="2" x2="12" y2="22"/>
                      <line x1="2" y1="12" x2="22" y2="12"/>
                    </svg>
                    <div class="flex flex-col">
                      <span class="text-[8px] font-mono text-slate-400">WIND LOAD</span>
                      <span class="text-[9px] font-mono font-bold text-slate-200">{{ settings.services_cad_wind_load || '120 KM/H OK' }}</span>
                    </div>
                  </div>
                </div>

                <!-- Bottom Specs Table in CAD style -->
                <div class="relative z-10 grid grid-cols-3 gap-2 border-t border-slate-900 pt-3">
                  <div class="bg-slate-900/50 p-2 rounded border border-slate-900 text-center">
                    <span class="block text-[7px] font-mono text-slate-500 uppercase">SPEC STRUCT</span>
                    <span class="text-[10px] font-mono font-bold text-slate-300">{{ settings.services_cad_spec_struct || 'PREFAB MOD' }}</span>
                  </div>
                  <div class="bg-slate-900/50 p-2 rounded border border-slate-900 text-center">
                    <span class="block text-[7px] font-mono text-slate-500 uppercase">ROOF COVER</span>
                    <span class="text-[10px] font-mono font-bold text-slate-300">{{ settings.services_cad_roof_cover || 'POLY 150MIC' }}</span>
                  </div>
                  <div class="bg-slate-900/50 p-2 rounded border border-slate-900 text-center">
                    <span class="block text-[7px] font-mono text-slate-500 uppercase">QC STATUS</span>
                    <span class="text-[10px] font-mono font-bold text-emerald-400">{{ settings.services_cad_qc_status || 'PASSED ISO' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </section>

      <!-- ═══ PERFORMANCE STATS DASHBOARD ═══ -->
      <section data-section="stats" class="relative -mt-20 z-20 max-w-6xl mx-auto px-4 sm:px-6">
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <div v-for="(stat, i) in stats" :key="i"
               class="relative overflow-hidden group bg-white/80 dark:bg-slate-900/70 backdrop-blur-xl border border-slate-200/50 dark:border-slate-800/80 p-6 rounded-2xl transition-all duration-500 hover:shadow-xl hover:shadow-emerald-500/5 dark:hover:shadow-emerald-500/10 hover:border-emerald-500/30 dark:hover:border-emerald-400/30 hover:-translate-y-1"
               :style="{ transitionDelay: i * 50 + 'ms' }"
               :class="{'animate-fade-up': visibleSections.has('stats')}">
            
            <!-- Floating corner status indicator -->
            <span class="absolute top-4 right-4 w-2 h-2 rounded-full bg-emerald-500 dark:bg-emerald-400 animate-pulse"></span>
            
            <!-- Graphic / Icon Box -->
            <div class="flex flex-col gap-4">
              <div class="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-900/40 flex items-center justify-center text-emerald-600 dark:text-emerald-400 transition-transform duration-500 group-hover:scale-110">
                <svg v-if="i === 0" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>
                <svg v-else-if="i === 1" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
                <svg v-else-if="i === 2" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                <svg v-else class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"/></svg>
              </div>
              
              <div>
                <p class="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600 dark:from-emerald-400 dark:to-teal-400 tracking-tight mb-1">
                  {{ stat.number }}
                </p>
                <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-bold tracking-wide uppercase">
                  {{ stat.label }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ═══ CONNECTED SERVICE LIFECYCLE ═══ -->
      <section class="py-20 md:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Section Header -->
        <div data-section="process-header" class="text-center mb-20 md:mb-28"
             :class="{'animate-fade-up': visibleSections.has('process-header')}">
          <span class="text-emerald-600 dark:text-emerald-400 text-sm font-bold tracking-[0.25em] uppercase">ขั้นตอนการดำเนินงาน</span>
          <h2 class="text-3xl md:text-5xl font-black text-slate-900 dark:text-white mt-4 tracking-tight">กระบวนการ 6 ขั้นตอนสู่ความสมบูรณ์แบบ</h2>
          <div class="mt-4 w-12 h-1 bg-emerald-500 rounded-full mx-auto"></div>
        </div>

        <div class="relative max-w-6xl mx-auto px-2 md:px-0">
          <!-- Central timeline path (Desktop only) -->
          <div class="absolute left-1/2 top-4 bottom-4 -translate-x-1/2 w-0.5 bg-gradient-to-b from-emerald-500/10 via-emerald-500/50 to-teal-500/10 hidden md:block"></div>
          
          <div class="space-y-16 md:space-y-28">
            <div v-for="(feature, index) in settings.services_items" :key="index"
                 :data-section="'step-'+index"
                 class="relative flex flex-col md:flex-row items-center gap-8 md:gap-16"
                 :class="[
                   index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                 ]"

                 :style="{ transitionDelay: '150ms' }">
              
              <!-- Central timeline node (Desktop only) -->
              <div class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:flex items-center justify-center z-20">
                <div class="w-10 h-10 rounded-full bg-slate-900 border-2 border-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/25">
                  <span class="text-[10px] font-mono text-emerald-400 font-bold">{{ String(index + 1).padStart(2, '0') }}</span>
                </div>
                <div class="absolute w-14 h-14 bg-emerald-500/15 rounded-full animate-ping pointer-events-none" style="animation-duration: 3s"></div>
              </div>

              <!-- Image Column -->
              <div class="w-full md:w-1/2 relative group">
                <div class="relative rounded-[2rem] overflow-hidden shadow-xl border border-slate-200/50 dark:border-slate-800/40">
                  <img :src="feature.image || serviceImages[index] || serviceImages[0]" :alt="feature.title"
                       class="w-full h-72 md:h-[340px] object-cover transform group-hover:scale-105 transition-transform duration-700" />
                  <!-- Gradient bottom scrim -->
                  <div class="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/10 to-transparent"></div>
                  
                  <!-- Floating tech specification tag -->
                  <div class="absolute top-4 left-4 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-2xl bg-slate-950/90 border border-slate-800 text-white backdrop-blur-md shadow-md">
                    <span class="text-emerald-400 font-mono font-bold text-[10px] tracking-wider uppercase">{{ feature.tech_label || getStepTechLabel(index) }}</span>
                  </div>

                  <!-- Visual system details -->
                  <div class="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white/90 font-mono">
                    <span class="text-[10px] tracking-widest text-emerald-300 font-semibold uppercase">SYSTEM: STABLE</span>
                    <span class="text-[9px] opacity-65">MODULE-REF // CR-0{{ index + 1 }}</span>
                  </div>
                </div>
                <!-- Glowing effect -->
                <div class="absolute -bottom-4 -right-4 w-36 h-36 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none"></div>
              </div>

              <!-- Content Column -->
              <div class="w-full md:w-1/2 flex flex-col justify-center">
                <div class="flex items-center gap-3.5 mb-4">
                  <div class="w-10 h-10 rounded-2xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900/40 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shadow-sm">
                    <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" :d="feature.icon"></path></svg>
                  </div>
                  <span class="text-xs font-extrabold text-emerald-600 dark:text-emerald-400 tracking-widest uppercase">ขั้นตอนที่ {{ index + 1 }}</span>
                </div>
                
                <h3 class="text-2xl md:text-3xl font-extrabold text-slate-900 dark:text-white mb-4 tracking-tight leading-snug">
                  {{ feature.title }}
                </h3>
                
                <p class="text-slate-600 dark:text-slate-355 leading-relaxed text-base md:text-lg mb-6">
                  {{ feature.desc }}
                </p>

                <!-- Tailored technical features checklist -->
                <ul class="space-y-3">
                  <li v-for="(check, cIdx) in (feature.checklist && feature.checklist.length > 0 ? feature.checklist : getStepChecklist(index))" :key="cIdx" class="flex items-start gap-3 text-sm text-slate-500 dark:text-slate-400">
                    <span class="w-5 h-5 rounded-full bg-emerald-100/60 dark:bg-emerald-950/40 border border-emerald-200/40 dark:border-emerald-900/30 flex items-center justify-center shrink-0 text-emerald-600 dark:text-emerald-400">
                      <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <span class="leading-snug">{{ check }}</span>
                  </li>
                </ul>
              </div>

            </div>
          </div>
        </div>
      </section>

      <!-- ═══ COMPARISON MATRIX ═══ -->
      <section class="py-24 md:py-32 bg-slate-100/40 dark:bg-slate-950/20 border-y border-slate-200/50 dark:border-slate-800/40 relative">
        <div class="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-emerald-500/5 via-transparent to-transparent pointer-events-none"></div>
        
        <div class="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <div class="text-center mb-16">
            <span class="text-emerald-600 dark:text-emerald-400 text-sm font-bold tracking-[0.25em] uppercase">{{ settings.services_comparison_subtitle || 'เปรียบเทียบมาตรฐาน' }}</span>
            <h2 class="text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white mt-3 tracking-tight">{{ settings.services_comparison_title || 'ทำไมต้องเลือกบริการออกแบบติดตั้งของเรา?' }}</h2>
            <p class="text-slate-500 dark:text-slate-400 mt-4 max-w-xl mx-auto text-base">{{ settings.services_comparison_desc || 'ยกระดับงานบริการติดตั้งโรงเรือนสู่มาตรฐานระดับสากล ปราณีตและแตกต่างอย่างเห็นได้ชัด' }}</p>
          </div>

          <!-- Comparison Mobile Cards (Visible < sm) -->
          <div class="sm:hidden space-y-4">
            <div v-for="(item, idx) in comparisonItems" :key="'mob-comp-'+idx" class="bg-white dark:bg-slate-950/80 border border-slate-200/60 dark:border-slate-800 rounded-2xl p-5 shadow-lg space-y-3">
              <h3 class="text-sm font-bold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800 pb-2">{{ item.feature }}</h3>
              <div class="space-y-2 text-xs">
                <div class="p-3 bg-emerald-500/10 dark:bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                  <div class="font-extrabold text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5 mb-1">
                    <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
                    CR Distribution
                  </div>
                  <p class="text-emerald-800 dark:text-emerald-300 font-medium leading-relaxed">{{ item.us }}</p>
                </div>
                <div class="p-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 rounded-xl">
                  <div class="font-bold text-slate-500 dark:text-slate-400 mb-1">ช่างทั่วไปตามท้องตลาด</div>
                  <p class="text-slate-600 dark:text-slate-400 leading-relaxed">{{ item.them }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Comparison Table Wrapper (Visible >= sm) -->
          <div class="hidden sm:block overflow-x-auto rounded-[2rem] border border-slate-200/60 dark:border-slate-800 shadow-2xl bg-white dark:bg-slate-950/80 backdrop-blur-md">
            <table class="w-full text-left border-collapse min-w-[700px]">
              <thead>
                <tr class="bg-slate-50 dark:bg-slate-900/30 border-b border-slate-100 dark:border-slate-800">
                  <th class="p-6 text-xs font-bold text-slate-400 font-mono tracking-wider uppercase w-1/3">คุณลักษณะเด่น</th>
                  <th class="p-6 text-sm font-extrabold text-emerald-700 dark:text-emerald-400 bg-emerald-500/5 dark:bg-emerald-500/5 w-1/3 border-x border-slate-100 dark:border-slate-800/80">
                    <div class="flex items-center gap-2">
                      <span class="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                      CR Distribution
                    </div>
                  </th>
                  <th class="p-6 text-sm font-bold text-slate-500 dark:text-slate-400 w-1/3">ช่างทั่วไปตามท้องตลาด</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
                <tr v-for="(item, idx) in comparisonItems" :key="idx" class="hover:bg-slate-50/50 dark:hover:bg-slate-900/20 transition-colors">
                  <td class="p-6 text-sm font-bold text-slate-900 dark:text-white">{{ item.feature }}</td>
                  <td class="p-6 text-sm font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-500/5 dark:bg-emerald-500/5 border-x border-slate-100 dark:border-slate-800/80">
                    {{ item.us }}
                  </td>
                  <td class="p-6 text-sm text-slate-500">{{ item.them }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- ═══ CTA BANNER ═══ -->
      <section data-section="cta" class="py-20 md:py-28 px-4"
               :class="{'animate-fade-up': visibleSections.has('cta')}">
        <div class="max-w-6xl mx-auto relative rounded-[2.5rem] overflow-hidden border border-emerald-500/20 dark:border-emerald-400/25 shadow-[0_20px_50px_rgba(16,185,129,0.12)]">
          <!-- Premium Backdrop and glowing shapes -->
          <div class="absolute inset-0 bg-gradient-to-br from-[#0b1b1a] via-[#060a0f] to-[#041416]"></div>
          <div class="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-500 via-transparent to-transparent"></div>
          <div class="absolute -top-20 -right-20 w-80 h-80 bg-emerald-500/15 rounded-full blur-3xl animate-pulse"></div>
          <div class="absolute -bottom-20 -left-20 w-80 h-80 bg-teal-500/15 rounded-full blur-3xl animate-pulse" style="animation-delay: 2s"></div>

          <div class="relative z-10 p-8 md:p-20 flex flex-col lg:flex-row items-center justify-between gap-12">
            <div class="flex-1 text-center lg:text-left">
              <!-- Live engineer consulting indicator -->
              <div class="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-wider mb-6">
                <span class="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping"></span>
                <span>วิศวกรพร้อมให้บริการปรึกษาฟรี</span>
              </div>
              
              <h2 class="text-3xl md:text-5xl font-black text-white mb-6 tracking-tight leading-[1.2]">
                {{ settings.services_cta_title }}
              </h2>
              <p class="text-emerald-100/70 text-base md:text-lg leading-relaxed max-w-xl mx-auto lg:mx-0">
                {{ settings.services_cta_desc }}
              </p>
            </div>
            
            <div class="flex flex-col sm:flex-row gap-4 shrink-0 w-full sm:w-auto justify-center">
              <router-link to="/quotation" class="group inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-bold py-4.5 px-8 rounded-2xl shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30 transition-all duration-300 hover:-translate-y-1">
                ขอใบเสนอราคาฟรี
                <svg class="w-5 h-5 group-hover:translate-x-1.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
              </router-link>
              
              <a v-if="settingsStore.contactLines.length > 0"
                 :href="settingsStore.contactLines[0].url || ('https://line.me/ti/p/~' + (settingsStore.contactLines[0].value || '').replace(/^@/, ''))"
                 target="_blank"
                 class="group inline-flex items-center justify-center gap-2.5 bg-[#00B900] hover:bg-[#009900] text-white font-bold py-4.5 px-8 rounded-2xl shadow-lg transition-all duration-300 hover:-translate-y-1">
                <svg class="w-5 h-5 group-hover:scale-110 transition-transform duration-300" fill="currentColor" viewBox="0 0 24 24"><path d="M24 10.304c0-5.369-5.383-9.738-12-9.738-6.616 0-12 4.369-12 9.738 0 4.814 3.966 8.887 9.539 9.613.385.082.906.262 1.042.6.12.3.05.748.024 1.036l-.16 1.94c-.039.232-.178 1.066.938.595 1.114-.47 6.012-3.542 8.441-6.234 2.802-3.09 4.176-5.834 4.176-7.55z"/></svg>
                คุยกับเราทาง LINE
              </a>
            </div>
          </div>
        </div>
      </section>

      <!-- ═══ RICH CONTENT PANEL ═══ -->
      <section v-if="settings.services_content_rich" class="py-20 max-w-5xl mx-auto px-4 sm:px-6">
        <div class="mb-12 text-center">
          <h2 class="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">รายละเอียดการให้บริการเชิงลึก</h2>
          <div class="mt-4 w-12 h-1 bg-emerald-500 rounded-full mx-auto"></div>
        </div>
        <div class="bg-white dark:bg-slate-950/40 rounded-3xl p-6 md:p-12 border border-slate-200/50 dark:border-slate-800/50 shadow-xl services-rich-content prose prose-emerald prose-lg dark:prose-invert max-w-none transition-colors duration-500" v-html="settings.services_content_rich"></div>
      </section>

    </div>
  </div>
</template>

<style scoped>
/* ── Animations ── */
.animate-fade-up {
  animation: fadeUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ── Rich Content Styles ── */
.services-rich-content :deep(h2), .services-rich-content :deep(h3), .services-rich-content :deep(h4) { color: #065f46; font-weight: 800; margin-top: 2.5rem; margin-bottom: 1rem; }
:global(.dark) .services-rich-content :deep(h2), :global(.dark) .services-rich-content :deep(h3), :global(.dark) .services-rich-content :deep(h4) { color: #34d399; }
.services-rich-content :deep(p) { color: #334155; line-height: 1.95; margin-bottom: 1.25rem; }
:global(.dark) .services-rich-content :deep(p) { color: #cbd5e1; }
.services-rich-content :deep(strong) { color: #047857; font-weight: 700; }
:global(.dark) .services-rich-content :deep(strong) { color: #6ee7b7; }
.services-rich-content :deep(ul), .services-rich-content :deep(ol) { padding-left: 1.75rem; margin-bottom: 1.25rem; }
.services-rich-content :deep(li) { margin-bottom: 0.75rem; line-height: 1.85; color: #475569; }
:global(.dark) .services-rich-content :deep(li) { color: #94a3b8; }
.services-rich-content :deep(li::marker) { color: #10b981; }
.services-rich-content :deep(a) { color: #059669; text-decoration: underline; transition: color 0.2s; }
.services-rich-content :deep(a:hover) { color: #047857; }
.services-rich-content :deep(table) { width: 100% !important; border-collapse: separate !important; border-spacing: 0 !important; border-radius: 16px; overflow: hidden; border: 1px solid #e2e8f0 !important; margin: 2rem auto; }
.services-rich-content :deep(table thead) { background: linear-gradient(135deg, #059669, #10b981); }
.services-rich-content :deep(table thead th) { color: white; font-weight: 700; padding: 16px 20px; text-transform: uppercase; font-size: 0.85rem; letter-spacing: 0.05em; }
.services-rich-content :deep(table tbody tr:nth-child(even)) { background-color: #f8fafc; }
.services-rich-content :deep(table tbody tr:hover) { background-color: #f1f5f9; }
.services-rich-content :deep(table tbody td) { padding: 14px 20px; border-bottom: 1px solid #f1f5f9; color: #334155; font-size: 0.95rem; }
:global(.dark) .services-rich-content :deep(table) { border-color: #1e293b !important; }
:global(.dark) .services-rich-content :deep(table thead) { background: linear-gradient(135deg, #064e3b, #047857); }
:global(.dark) .services-rich-content :deep(table tbody tr:nth-child(even)) { background-color: #0f172a/30; }
:global(.dark) .services-rich-content :deep(table tbody tr:hover) { background-color: #1e293b/50; }
:global(.dark) .services-rich-content :deep(table tbody td) { border-bottom-color: #1e293b; color: #94a3b8; }
.services-rich-content :deep(figure.table) { display: flex; justify-content: center; margin: 2.5rem auto; width: 100%; }
</style>
